import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  CheckCircle2, XCircle, Loader2, ArrowLeft, Home, 
  CreditCard, Shield, Clock, Sparkles, Star, Crown,
  Building2, Gift, Zap
} from "lucide-react";
import { toast } from "sonner";
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PaymentResult = ({ type }) => {
  const [searchParams] = useSearchParams();
  const { token, refreshUser, user } = useAuth();
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');
  const attemptsRef = useRef(0);
  const maxAttempts = 10;
  const pollInterval = 2000;
  const [progress, setProgress] = useState(0);
  
  const [status, setStatus] = useState(type === 'cancelled' ? 'cancelled' : 'loading');

  useEffect(() => {
    if (type !== 'success' || !sessionId || !token) {
      return;
    }

    let timeoutId;
    let cancelled = false;

    const checkStatus = async () => {
      if (cancelled) return;
      
      // Update progress
      setProgress((attemptsRef.current / maxAttempts) * 100);
      
      if (attemptsRef.current >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const response = await axios.get(
          `${API}/subscription/checkout/status/${sessionId}`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        if (cancelled) return;

        const data = response.data;
        setPaymentInfo(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          setProgress(100);
          toast.success('Pagamento confirmado! Seu plano foi ativado.');
          if (refreshUser) {
            await refreshUser();
          }
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        attemptsRef.current += 1;
        timeoutId = setTimeout(checkStatus, pollInterval);
      } catch (error) {
        if (cancelled) return;
        console.error('Error checking payment status:', error);
        attemptsRef.current += 1;
        if (attemptsRef.current < maxAttempts) {
          timeoutId = setTimeout(checkStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [type, sessionId, token, refreshUser]);

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const handleGoToPricing = () => {
    window.location.href = '/?view=pricing';
  };

  const getPlanIcon = (plan) => {
    switch(plan) {
      case 'empresarial': return <Building2 className="w-8 h-8" />;
      case 'premium': return <Crown className="w-8 h-8" />;
      case 'basico': return <Star className="w-8 h-8" />;
      default: return <Sparkles className="w-8 h-8" />;
    }
  };

  const getPlanName = (plan) => {
    const names = {
      'empresarial': 'Empresarial',
      'premium': 'Premium',
      'basico': 'Essencial',
      'free': 'Gratuito'
    };
    return names[plan] || plan;
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a0a0a 0%, #111 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <Card style={{
        maxWidth: '500px',
        width: '100%',
        background: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '16px',
        overflow: 'hidden'
      }}>
        {/* Loading State */}
        {status === 'loading' && (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #00ffff20 0%, #0066ff20 100%)',
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #00ffff 0%, #0066ff 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem',
                animation: 'pulse 2s infinite'
              }}>
                <CreditCard className="w-10 h-10" style={{ color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
                Verificando Pagamento
              </h2>
              <p style={{ color: '#888', fontSize: '0.9rem' }}>
                Aguarde enquanto confirmamos sua transação...
              </p>
            </div>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#888', fontSize: '0.85rem' }}>Processando</span>
                  <span style={{ color: '#00ffff', fontSize: '0.85rem' }}>{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} style={{ height: '6px' }} />
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1rem',
                background: '#0f0f0f',
                borderRadius: '8px'
              }}>
                <Shield className="w-5 h-5" style={{ color: '#22c55e' }} />
                <span style={{ color: '#888', fontSize: '0.85rem' }}>
                  Transação segura via Stripe
                </span>
              </div>
            </CardContent>
          </>
        )}

        {/* Success State */}
        {status === 'success' && (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #22c55e20 0%, #16a34a20 100%)',
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <CheckCircle2 className="w-10 h-10" style={{ color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
                Pagamento Confirmado! 🎉
              </h2>
              <p style={{ color: '#888', fontSize: '0.9rem' }}>
                Seu plano foi ativado com sucesso
              </p>
            </div>
            <CardContent style={{ padding: '1.5rem' }}>
              {paymentInfo && (
                <div style={{
                  background: '#0f0f0f',
                  borderRadius: '12px',
                  padding: '1.25rem',
                  marginBottom: '1.5rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, #00ffff 0%, #0066ff 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {getPlanIcon(paymentInfo.plan_id)}
                    </div>
                    <div>
                      <p style={{ color: '#fff', fontWeight: '600', fontSize: '1.1rem' }}>
                        Plano {getPlanName(paymentInfo.plan_id)}
                      </p>
                      <p style={{ color: '#888', fontSize: '0.85rem' }}>
                        Assinatura mensal ativa
                      </p>
                    </div>
                  </div>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '1rem',
                    paddingTop: '1rem',
                    borderTop: '1px solid #333'
                  }}>
                    <div>
                      <p style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Valor</p>
                      <p style={{ color: '#22c55e', fontWeight: '700', fontSize: '1.1rem' }}>
                        R$ {paymentInfo.amount?.toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                    <div>
                      <p style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Status</p>
                      <p style={{ color: '#22c55e', fontWeight: '600', fontSize: '0.9rem' }}>
                        ✓ Confirmado
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Benefits reminder */}
              <div style={{
                background: 'rgba(0, 255, 255, 0.05)',
                border: '1px solid rgba(0, 255, 255, 0.2)',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                  <Gift className="w-4 h-4" style={{ color: '#00ffff' }} />
                  <span style={{ color: '#00ffff', fontWeight: '600', fontSize: '0.85rem' }}>
                    Benefícios Ativados
                  </span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Zap className="w-3 h-3" style={{ color: '#888' }} />
                    <span style={{ color: '#aaa', fontSize: '0.8rem' }}>Créditos mensais recarregados</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Zap className="w-3 h-3" style={{ color: '#888' }} />
                    <span style={{ color: '#aaa', fontSize: '0.8rem' }}>Acesso a todas as análises</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Zap className="w-3 h-3" style={{ color: '#888' }} />
                    <span style={{ color: '#aaa', fontSize: '0.8rem' }}>Suporte prioritário</span>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleGoHome} 
                style={{
                  width: '100%',
                  background: 'linear-gradient(135deg, #00ffff 0%, #0066ff 100%)',
                  color: '#000',
                  fontWeight: '600',
                  padding: '0.75rem',
                  borderRadius: '8px'
                }}
              >
                <Home className="w-4 h-4 mr-2" />
                Começar a Usar
              </Button>
            </CardContent>
          </>
        )}

        {/* Cancelled State */}
        {status === 'cancelled' && (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #f5920520 0%, #ea580c20 100%)',
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <XCircle className="w-10 h-10" style={{ color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
                Pagamento Cancelado
              </h2>
              <p style={{ color: '#888', fontSize: '0.9rem' }}>
                Você cancelou o processo de pagamento
              </p>
            </div>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{
                background: '#0f0f0f',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <Shield className="w-5 h-5" style={{ color: '#22c55e' }} />
                <span style={{ color: '#888', fontSize: '0.85rem' }}>
                  Não se preocupe, nenhum valor foi cobrado
                </span>
              </div>
              
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <Button 
                  onClick={handleGoToPricing} 
                  variant="outline"
                  style={{ flex: 1 }}
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Ver Planos
                </Button>
                <Button 
                  onClick={handleGoHome}
                  style={{ flex: 1 }}
                >
                  <Home className="w-4 h-4 mr-2" />
                  Início
                </Button>
              </div>
            </CardContent>
          </>
        )}

        {/* Expired State */}
        {status === 'expired' && (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #ef444420 0%, #dc262620 100%)',
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Clock className="w-10 h-10" style={{ color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
                Sessão Expirada
              </h2>
              <p style={{ color: '#888', fontSize: '0.9rem' }}>
                O tempo para completar o pagamento expirou
              </p>
            </div>
            <CardContent style={{ padding: '1.5rem' }}>
              <Button 
                onClick={handleGoToPricing} 
                style={{ width: '100%' }}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Tentar Novamente
              </Button>
            </CardContent>
          </>
        )}

        {/* Timeout State */}
        {status === 'timeout' && (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #f5920520 0%, #ea580c20 100%)',
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <Loader2 className="w-10 h-10" style={{ color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
                Verificação em Andamento
              </h2>
              <p style={{ color: '#888', fontSize: '0.9rem' }}>
                Estamos processando seu pagamento
              </p>
            </div>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{
                background: '#0f0f0f',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ color: '#888', fontSize: '0.85rem', textAlign: 'center' }}>
                  Você receberá uma confirmação por email em breve. 
                  Seu plano será ativado automaticamente assim que o pagamento for confirmado.
                </p>
              </div>
              <Button onClick={handleGoHome} style={{ width: '100%' }}>
                <Home className="w-4 h-4 mr-2" />
                Ir para o App
              </Button>
            </CardContent>
          </>
        )}

        {/* Error State */}
        {status === 'error' && (
          <>
            <div style={{
              background: 'linear-gradient(135deg, #ef444420 0%, #dc262620 100%)',
              padding: '3rem 2rem',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <XCircle className="w-10 h-10" style={{ color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
                Erro na Verificação
              </h2>
              <p style={{ color: '#888', fontSize: '0.9rem' }}>
                Não foi possível verificar o status do pagamento
              </p>
            </div>
            <CardContent style={{ padding: '1.5rem' }}>
              <div style={{
                background: '#0f0f0f',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ color: '#888', fontSize: '0.85rem', textAlign: 'center' }}>
                  Se você completou o pagamento, ele será processado automaticamente.
                  Entre em contato conosco se precisar de ajuda.
                </p>
              </div>
              <Button onClick={handleGoHome} style={{ width: '100%' }}>
                <Home className="w-4 h-4 mr-2" />
                Ir para o App
              </Button>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default PaymentResult;
